<script setup lang="ts">
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { nanoid } from "nanoid";
import type { DefaultInputProps, IMultiSelectItem } from "@/interfaces";
import { toRefs } from "vue";

const id = nanoid();

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  modelValue?: string;
  items: IMultiSelectItem[];
  allowDeselect?: boolean;
  gridTemplate?: string;
}

const props = withDefaults(defineProps<Props>(), {
  allowDeselect: true,
  gridTemplate: "grid-cols-3 gap-3 tablet:grid-cols-4",
});

const { modelValue, allowDeselect } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);

const handleSelect = (item: IMultiSelectItem) => {
  if (modelValue.value === item.id && allowDeselect.value) {
    emit("update:modelValue", undefined);
  } else {
    emit("update:modelValue", item.id);
  }
};
</script>

<template>
  <InputWrapper
    :forId="id"
    :label="label"
    :description="description"
    :isRequired="isRequired"
    :inputKey="inputKey"
    :tooltip="tooltip"
    :disabled="disabled"
    :name="name"
    :validation="validation"
    :errorMessageKey="errorMessageKey"
  >
    <div class="grid" :class="[gridTemplate]">
      <button
        type="button"
        role="radio"
        v-for="item of items"
        :key="item.id"
        class="focus-visible:outline-neutral1000 outline rounded-lg p-3 pr-4 flex items-center gap-2"
        :class="{
          'bg-white outline-neutral300': modelValue !== item.id,
          'bg-darkOcean100 outline-darkOcean600 outline-2':
            modelValue === item.id,
        }"
        @click="() => handleSelect(item)"
      >
        <div class="h300 text-neutral1000 text-center w-full">
          <slot name="title" :item="item">
            {{ item.title }}
          </slot>
        </div>
      </button>
    </div>
  </InputWrapper>
</template>
